import { yupResolver } from "@hookform/resolvers/yup";
import { Close } from '@mui/icons-material';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Grid, IconButton, Modal, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../Hooks/useToast';
import { useUser } from '../../Hooks/useUser';
import { useAddJobMutation, useUpdateJobMutation } from '../../Redux/api/jobApi';
import { useLazyCreateSetupIntentQuery, usePaymentRequestMutation } from '../../Redux/api/stripeApi';
import { setShowAddPaymentMethodModal } from '../../Redux/slices/GlobalModalSlice';
import { setClientSecret, setCustomerSessionClientSecret } from '../../Redux/slices/GlobalSlice';
import { reset, setDeliverables, setDuration, setDurationType, setJobPostingModalOpen, setNextDisabled, setPayAmount, setPayRate, setPrimaryLanguage, setQualifications, setQuestions, setResponsibilities, setStep, setTitle, setType, setWorkSite } from '../../Redux/slices/JobPostingCreationSlice';
import { useAppDispatch, useAppSelector } from '../../Redux/store';
import { JobPostingModalSchema } from '../../Schemas/JobPostingSchemas';
import { AddJobRequest, JobCreationSteps, JobStatus, SalaryType } from '../../Types/Job';
import { removeNonDigits } from '../../Utils/String';
import { KaziButton } from '../Kazi/KaziButton';
import { Deliverables } from './Deliverables/Deliverables';
import { JobDescription } from './JobDescription';
import { Qualifications } from './Qualifications/Qualifications';
import { Questions } from './Questions/Questions';
import { Responsibilities } from './Responsibilities/Responsibilities';
import { TalentPreference } from './TalentPreference';
import { UpgradeSubscriptionModal } from './UpgradeSubscriptionModal';
import { useTalentFilters } from '../../Hooks/useTalentFilters';

export const CreateJobPostingModal = () => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const jobPostingCreationSlice = useAppSelector( state => state.JobPostingCreation);
    const { hasDefaultPaymentMethod } = useUser();
    const { showToast } = useToast();
    const { invalidateUser } = useUser();
    const [shortTerm, setShortTem] = useState(false);
    const [isLastStep, setIsLastStep] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingJobLocator, setEditingJobLocator] = useState<string | null>(null);
    const [addJob, {data: jobResponse, isLoading: jobResponseLoading}] = useAddJobMutation();
    const [updateJob, {isLoading: updateJobLoading}] = useUpdateJobMutation();
    const [  createSetupIntent ] = useLazyCreateSetupIntentQuery();
    const [paymentRequest] = usePaymentRequestMutation();
    const { getSpecialization } = useTalentFilters();

    const jobPostingForm = useForm({
        defaultValues: {
            type: jobPostingCreationSlice.type ?? '',
            title: jobPostingCreationSlice.title ?? '',
            work_site: jobPostingCreationSlice.work_site ?? '',
            primary_language: jobPostingCreationSlice.primary_language ?? '',
            pay_amount: {
                pay_type: jobPostingCreationSlice.pay_amount?.pay_type ?? 'Fixed',
                min: jobPostingCreationSlice.pay_amount?.min ?? '',
                max: jobPostingCreationSlice.pay_amount?.max ?? ''
            },
            pay_rate: jobPostingCreationSlice.pay_rate ?? '',
            duration: jobPostingCreationSlice.duration ?? '',
            duration_type: jobPostingCreationSlice.duration_type ?? '',
            responsibilities: jobPostingCreationSlice.responsibilities ?? [],
            qualifications: jobPostingCreationSlice.qualifications ?? [],
            questions: jobPostingCreationSlice.questions ?? [],
            deliverables: jobPostingCreationSlice.deliverables ?? []
        },
        resolver: yupResolver(JobPostingModalSchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    });
    const {
        control,
        formState: {isDirty, isValid, errors},
        getValues,
        setValue,
        watch
    } = jobPostingForm;
    /* ******************** Variables ******************** */
    const userId = useAppSelector( state => state.Global.userId)
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const formDirty = jobPostingCreationSlice.isDirty || isDirty
    /* ******************** Functions ******************** */
    const getContent = () => {
        switch(jobPostingCreationSlice.step){
            case JobCreationSteps.Preferences:
                return <TalentPreference form={jobPostingForm}/>;
            case JobCreationSteps.Description:
                return <JobDescription />;
            case JobCreationSteps.Responsibilities:
                return <Responsibilities control={control} />;
            case JobCreationSteps.Qualifications:
                return <Qualifications control={control} />;
            case JobCreationSteps.Deliverables:
                if (shortTerm) {
                    return <Deliverables control={control} />;
                }
            case JobCreationSteps.Questions:
                return <Questions control={control} />;
        }
    }
    const handleFormChange = () => {
        if (jobPostingCreationSlice.step === JobCreationSteps.Preferences) {
            if (isValid) {
                dispatch(setTitle(getValues('title')))
                dispatch(setType(getValues('type')))
                dispatch(setWorkSite(getValues('work_site')))
                dispatch(setPrimaryLanguage(getValues('primary_language')))
                dispatch(setPayAmount({
                    pay_type: jobPostingCreationSlice.pay_amount?.pay_type ?? 'Fixed',
                    min: removeNonDigits(getValues('pay_amount')?.min ?? ''),
                    max: removeNonDigits(getValues('pay_amount')?.max ?? '')
                }))
                dispatch(setPayRate(getValues('pay_rate')))
                dispatch(setDuration(getValues('duration')))
                dispatch(setDurationType(getValues('duration_type')))
            }
        }
        if (jobPostingCreationSlice.step === JobCreationSteps.Responsibilities) {
            if (isValid) dispatch(setResponsibilities(Array.from(getValues('responsibilities')!)));
        }        
        if (jobPostingCreationSlice.step === JobCreationSteps.Qualifications) {
            const qualifications = Array.from(getValues('qualifications')!)
            if (qualifications.length === 1 && qualifications[0] === '') {
                setValue('qualifications', [])
            } else if (isValid && qualifications!.length > 0) dispatch(setQualifications(qualifications));
        }        
        if (jobPostingCreationSlice.step === JobCreationSteps.Deliverables) {
            if (shortTerm) {
                const deliverables = Array.from(getValues('deliverables')!)
                if (deliverables.length === 1 && deliverables[0] === '') {
                    setValue('deliverables', [])
                } else if (isValid && deliverables!.length > 0) dispatch(setDeliverables(deliverables));

            } 
        }
    }
    const handleClose = () =>{
        dispatch(reset());
        dispatch(setJobPostingModalOpen(false))
        setIsEditing(false);
        setEditingJobLocator(null);
    }
    const handleBack = () =>{
        handleFormChange();
        const prevStep = jobPostingCreationSlice.step-1
        if (prevStep >= 1) {
            dispatch(setStep(prevStep))
        } 
    }
    const handleNext = async () =>{
        // TODO: reset slice state on close
        if (!jobPostingCreationSlice.hasMembership){
            dispatch(setJobPostingModalOpen(false))
            setUpgradeOpen(true)
            return
        }
        handleFormChange();
        const nextStep = jobPostingCreationSlice.step+1
        if (nextStep <= 6 && !isLastStep) {
            dispatch(setStep(nextStep))
        } 
        if (isLastStep) {
            // if (jobPostingCreationSlice.pay_amount && jobPostingCreationSlice.title) {
            //     await paymentRequest({
            //         price: Number(jobPostingCreationSlice.pay_amount),
            //         project_name: jobPostingCreationSlice.title
            //     })
            //     .unwrap()
            //     .then(res => {

            //     })
            //     .catch(err => {

            //     })
            // }
            const questions = Array.from(getValues('questions')!)
            if (questions.length === 1 && questions[0] === '') {
                setValue('questions', [])
            } else if (isValid && questions!.length > 0) dispatch(setQuestions(questions));
            
            // Get current form values for pay amount
            const currentFormValues = getValues();
            const payAmountMin = removeNonDigits(currentFormValues.pay_amount?.min ?? '');
            const payAmountMax = removeNonDigits(currentFormValues.pay_amount?.max ?? '');
            
            // Update Redux state with current form values before updating
            if (isEditing && editingJobLocator) {
                dispatch(setTitle(currentFormValues.title ?? ''));
                dispatch(setType(currentFormValues.type ?? ''));
                dispatch(setWorkSite(currentFormValues.work_site ?? ''));
                dispatch(setPrimaryLanguage(currentFormValues.primary_language ?? ''));
                dispatch(setPayAmount({
                    pay_type: (currentFormValues.pay_amount?.pay_type as SalaryType | undefined) ?? 'Fixed',
                    min: currentFormValues.pay_amount?.min ?? '',
                    max: currentFormValues.pay_amount?.max ?? ''
                }));
                dispatch(setPayRate(currentFormValues.pay_rate ?? ''));
                dispatch(setDuration(currentFormValues.duration ?? ''));
                dispatch(setDurationType(currentFormValues.duration_type ?? ''));
                dispatch(setResponsibilities(currentFormValues.responsibilities ?? []));
                dispatch(setQualifications(currentFormValues.qualifications ?? []));
                dispatch(setQuestions(questions.map(q => q)));
                dispatch(setDeliverables(currentFormValues.deliverables ?? []));
            }
            
            if (isEditing && editingJobLocator) {
                const updateJobRequest = {
                    locator: editingJobLocator,
                    updated_data: {
                        title: currentFormValues.title ?? '',
                        level: 'Flexible',
                        type: currentFormValues.type ?? '',
                        tags: jobPostingCreationSlice.skills ?? [],
                        description: jobPostingCreationSlice.description ?? '',
                        responsibilities: currentFormValues.responsibilities ?? [],
                        skills: jobPostingCreationSlice.qualifications.map( qual => ({skill: qual, qualification: 'must_have'})) ?? [],
                        pay_type: currentFormValues.pay_amount?.pay_type as SalaryType | undefined,
                        pay_amount: Number(payAmountMin),
                        pay_amount_max: Number(payAmountMax),
                        pay_rate: currentFormValues.pay_rate ?? '',    
                        pay_currency: 'USD',
                        duration: Number(currentFormValues.duration ?? 30),
                        duration_type: currentFormValues.duration_type,
                        work_site: currentFormValues.work_site ?? '',
                        primary_language: currentFormValues.primary_language ?? '',
                        questions: questions.map( (q, idx) => ({id: idx, question: q})),
                        status: JobStatus.Open,
                        specialization: getSpecialization(currentFormValues.title!),
                        creation_method: 'manual'
                    }
                };
                
                updateJob(updateJobRequest)
                .unwrap()
                .then(res => {
                    invalidateUser();
                    handleClose();
                    navigate(`/job-postings/posting/${editingJobLocator}`)
                })
                .catch(err => {
                    console.error('Error occured while updating job: ', err)
                    showToast({
                        text: "Unable to update job",
                        variant: 'error'
                    })
                })
            } else {
                // Create new job
                const addJobRequest: AddJobRequest = {
                    client_locator: userId!,
                    title: currentFormValues.title ?? '',
                    level: 'Flexible',
                    type: currentFormValues.type ?? '',
                    tags: jobPostingCreationSlice.skills ?? [],
                    description: jobPostingCreationSlice.description ?? '',
                    responsibilities: currentFormValues.responsibilities ?? [],
                    deliverables: currentFormValues.deliverables ?? [],
                    skills: jobPostingCreationSlice.qualifications.map( qual => ({skill: qual, qualification: 'must_have'})) ?? [],
                    pay_type: currentFormValues.pay_amount?.pay_type as SalaryType | undefined,
                    pay_amount: Number(payAmountMin),
                    pay_amount_max: Number(payAmountMax),
                    pay_rate: currentFormValues.pay_rate ?? '',    
                    pay_currency: 'USD',
                    duration: Number(currentFormValues.duration ?? 30),
                    duration_type: currentFormValues.duration_type,
                    work_site: currentFormValues.work_site ?? '',
                    primary_language: currentFormValues.primary_language ?? '',
                    questions: questions.map( (q, idx) => ({id: idx, question: q})),
                    status: JobStatus.Open,
                    specialization: getSpecialization(currentFormValues.title!),
                    creation_method: 'manual'
                }
                // console.log('Add Job Request: ', addJobRequest);    
                addJob(addJobRequest)
                .unwrap()
                .then(res => {
                    invalidateUser();
                    handleClose();
                    navigate(`/job-postings/posting/${res.createJob.locator}`)
                })
                .catch(err => {
                    console.error('Error occured while creating job: ', err)
                    showToast({
                        text: "Unable to create job",
                        variant: 'error'
                    })
                })
            }
        }
    }
    const handleAddPaymentMethod = () => {
        if (!hasDefaultPaymentMethod) {
            createSetupIntent(userId!)
            .unwrap()
            .then( res => {
                dispatch(setClientSecret(res.clientSecret));
                dispatch(setCustomerSessionClientSecret(res.clientSecret));
                dispatch(setShowAddPaymentMethodModal(true))
            })
            .catch( err => {
                console.error("Error occured while creating setup intent: ", err);
                showToast({
                    text: "Unable to connect payment",
                    variant: "error"
                })
            })
        }
    }
    const handleSave = async () => {
        handleFormChange();
        const questions = Array.from(getValues('questions')!)
        if (questions.length === 1 && questions[0] === '') {
            setValue('questions', [])
        } else if (isValid && questions!.length > 0) dispatch(setQuestions(questions));
        const values = getValues();
        if ((values?.type?.length ?? 0) === 0) {
            showToast({
                text: 'Type of position is required',
                variant: 'error'
            })
            return
        }
        if ((values?.pay_rate?.length ?? 0) === 0) {
            showToast({
                text: 'Payment type is required',
                variant: 'error'
            })
            return
        }

        // Update Redux state with current form values before saving
        if (isEditing && editingJobLocator) {
            dispatch(setTitle(values.title ?? ''));
            dispatch(setType(values.type ?? ''));
            dispatch(setWorkSite(values.work_site ?? ''));
            dispatch(setPrimaryLanguage(values.primary_language ?? ''));
            dispatch(setPayAmount({
                pay_type: (values.pay_amount?.pay_type as SalaryType | undefined) ?? 'Fixed',
                min: values.pay_amount?.min ?? '',
                max: values.pay_amount?.max ?? ''
            }));
            dispatch(setPayRate(values.pay_rate ?? ''));
            dispatch(setDuration(values.duration ?? ''));
            dispatch(setDurationType(values.duration_type ?? ''));
            dispatch(setResponsibilities(values.responsibilities ?? []));
            dispatch(setQualifications(values.qualifications ?? []));
            dispatch(setQuestions(questions.map(q => q)));
            dispatch(setDeliverables(values.deliverables ?? []));
        }

        if (isEditing && editingJobLocator) {
            // Update existing job as draft
            const updateJobRequest = {
                locator: editingJobLocator,
                updated_data: {
                    title: values.title ?? ' ',
                    level: 'Flexible',
                    type: values.type ?? ' ',
                    tags: jobPostingCreationSlice.skills ?? [],
                    description: jobPostingCreationSlice.description ?? ' ',
                    responsibilities: values.responsibilities ?? [],
                    skills: jobPostingCreationSlice.qualifications.map( qual => ({skill: qual, qualification: 'must_have'})) ?? [],
                    pay_type: values.pay_amount?.pay_type as SalaryType | undefined,
                    pay_amount: Number(removeNonDigits(values.pay_amount?.min ?? '')),
                    pay_amount_max: Number(removeNonDigits(values.pay_amount?.max ?? '')),
                    pay_rate: values.pay_rate ?? ' ',    
                    pay_currency: 'USD',
                    duration: Number(values.duration ?? 30),
                    duration_type: (values?.duration_type?.length ?? 0) > 0 ? values.duration_type : undefined,
                    work_site: values?.work_site ?? ' ',
                    primary_language: values?.primary_language ?? ' ',
                    questions: questions.map( (q, idx) => ({id: idx, question: q})),
                    status: JobStatus.Draft,
                    specialization: getSpecialization(values.title!),
                    creation_method: 'manual'
                }
            };

            updateJob(updateJobRequest)
            .unwrap()
            .then(res => {
                invalidateUser();
                handleClose();
                navigate(`/job-postings`)
            })
            .catch(err => {
                console.error('Error occured while updating job: ', err)
                showToast({
                    text: "Unable to update job",
                    variant: 'error'
                })
            })
        } else {
            // Create new job as draft
            const saveDraft: AddJobRequest = {
                client_locator: userId!,
                title: values.title ?? ' ',
                level: 'Flexible',
                type: values.type ?? ' ',
                tags: jobPostingCreationSlice.skills ?? [],
                description: jobPostingCreationSlice.description ?? ' ',
                responsibilities: values.responsibilities ?? [],
                skills: jobPostingCreationSlice.qualifications.map( qual => ({skill: qual, qualification: 'must_have'})) ?? [],
                pay_type: values.pay_amount?.pay_type as SalaryType | undefined,
                pay_amount: Number(removeNonDigits(values.pay_amount?.min ?? '')),
                pay_amount_max: Number(removeNonDigits(values.pay_amount?.max ?? '')),
                pay_rate: values.pay_rate ?? ' ',    
                pay_currency: 'USD',
                duration: Number(values.duration ?? 30),
                duration_type: (values?.duration_type?.length ?? 0) > 0 ? values.duration_type : undefined,
                work_site: values?.work_site ?? ' ',
                primary_language: values?.primary_language ?? ' ',
                questions: questions.map( (q, idx) => ({id: idx, question: q})),
                status: JobStatus.Draft,
                specialization: getSpecialization(values.title!),
                creation_method: 'manual'
            }

                
            addJob(saveDraft)
            .unwrap()
            .then(res => {
                invalidateUser();
                handleClose();
                navigate(`/job-postings`)
            })
            .catch(err => {
                console.error('Error occured while saving job: ', err)
                showToast({
                    text: "Unable to save job",
                    variant: 'error'
                })
            })
        }
    }
    const getButton = () => {
        if (isLastStep && !hasDefaultPaymentMethod) {
            return (
                <Button 
                    variant={'contained'} 
                    onClick={handleAddPaymentMethod} 
                    disabled={jobPostingCreationSlice.nextDisabled}
                    sx={{
                        "&.Mui-disabled": {
                            backgroundColor: 'black.50',
                            border: 'none',
                            color: 'primary.main'
                        }
                    }}
                >                                            
                    Add payment method
                </Button>
            )
        } else {
            return (
                <KaziButton      
                    text={isLastStep ? (isEditing ? 'Update' : 'Post') : 'Next'} 
                    loading={{
                        isLoading: jobResponseLoading || updateJobLoading,
                        loadingText: isEditing ? 'Updating...' : 'Posting...',
                        color: 'secondary'
                    }}          
                    variant={'contained'} 
                    onClick={handleNext} 
                    disabled={jobPostingCreationSlice.nextDisabled}
                    sx={{
                        "&.Mui-disabled": {
                            backgroundColor: 'black.50',
                            border: 'none',
                            color: 'primary.main'
                        }
                    }}
                />
            )
        }
    }
    /* ******************** Effects ******************** */
    useEffect(() => {
        if (jobPostingCreationSlice?.type === 'Short-term' && shortTerm === false) setShortTem(true);
        if (jobPostingCreationSlice?.type !== 'Short-term' && shortTerm === true) setShortTem(false);

        if (jobPostingCreationSlice.step == JobCreationSteps.Questions || (!shortTerm && jobPostingCreationSlice.step == JobCreationSteps.Deliverables)) setIsLastStep(true);        
        if (jobPostingCreationSlice.step != JobCreationSteps.Questions && !(!shortTerm && jobPostingCreationSlice.step == JobCreationSteps.Deliverables)) setIsLastStep(false);
    }, [jobPostingCreationSlice?.type, jobPostingCreationSlice.step])
    useEffect(()=> { 
        if (isValid && jobPostingCreationSlice.nextDisabled) {
            dispatch(setNextDisabled(false));
        } 
        if (!isValid && !jobPostingCreationSlice.nextDisabled) {
            dispatch(setNextDisabled(true));
        }   
    }, [isValid]);

    // Update form when Redux state changes (for editing drafts)
    useEffect(() => {
        if (jobPostingCreationSlice.isOpen && jobPostingCreationSlice.isDirty) {
            setValue('type', jobPostingCreationSlice.type ?? '');
            setValue('title', jobPostingCreationSlice.title ?? '');
            setValue('work_site', jobPostingCreationSlice.work_site ?? '');
            setValue('primary_language', jobPostingCreationSlice.primary_language ?? '');
            setValue('pay_amount', {
                pay_type: jobPostingCreationSlice.pay_amount?.pay_type ?? 'Fixed',
                min: jobPostingCreationSlice.pay_amount?.min ?? '',
                max: jobPostingCreationSlice.pay_amount?.max ?? ''
            });
            setValue('pay_rate', jobPostingCreationSlice.pay_rate ?? '');
            setValue('duration', jobPostingCreationSlice.duration ?? '');
            setValue('duration_type', jobPostingCreationSlice.duration_type ?? '');
            setValue('responsibilities', jobPostingCreationSlice.responsibilities ?? []);
            setValue('qualifications', jobPostingCreationSlice.qualifications ?? []);
            setValue('questions', jobPostingCreationSlice.questions ?? []);
            setValue('deliverables', jobPostingCreationSlice.deliverables ?? []);
            
            // Set editing state if we have a title (indicating it's an existing job)
            if (jobPostingCreationSlice.title) {
                setIsEditing(true);
                setEditingJobLocator(jobPostingCreationSlice.jobLocator ?? null);
            }
        } else {
            setIsEditing(false);
            setEditingJobLocator(null);
        }
    }, [jobPostingCreationSlice.isOpen, jobPostingCreationSlice.isDirty, setValue]);
    /* ******************** JSX ******************** */
    if (!userId) return;
    return (
        <>
            <Modal
                open={jobPostingCreationSlice.isOpen}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Card
                    sx={{
                        width: "600px",
                        borderRadius: "12px",
                        maxHeight: "95%",
                        overflowY: 'auto'
                    }}
                >
                    <CardHeader 
                        sx={{
                            paddingBottom: 1
                        }}                
                        action={
                            <IconButton onClick={handleClose}>
                                <Close color='primary'/>
                            </IconButton>
                        }
                    />
                    <CardContent
                        sx={{
                            padding: '0 24px',
                            overflowY: 'auto',
                            maxHeight: '535px'
                        }}
                    >                    
                        <Grid container gap={'20px'}>
                            {getContent()}
                        </Grid>
                    </CardContent>
                    <CardActions
                        sx={{
                            padding: '24px'
                        }}
                    >
                        <Box display={'flex'} flexDirection={'row'} width={'100%'}>
                            {
                                // isUploading || talentUpdateResponseLoading ?
                                // null :
                                <>
                                    <Box flex={1} display={'flex'} alignItems={'center'}>
                                        <Typography variant={'body-small'} color={'black.300'}>Step {jobPostingCreationSlice.step} of {shortTerm? 6 : 5}</Typography>
                                    </Box>
                                    <Box flex={2} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={1}>
                                        <Button variant={'text'} onClick={handleSave} disabled={!formDirty}>Save as draft</Button>
                                        {isLastStep && hasDefaultPaymentMethod && <Button variant={'text'} onClick={handleNext}>Skip and post job</Button>}
                                        { jobPostingCreationSlice.step > 1 && <Button variant={'outlined'} onClick={handleBack}>Back</Button>}
                                        {getButton()}
                                    </Box>
                                </>
                            }
                        </Box>
                    </CardActions>
                </Card> 
            </Modal>
            <UpgradeSubscriptionModal 
                open={upgradeOpen}
                setOpen={setUpgradeOpen}
            />
        </>
    )
}
